import { ArrowDown, ArrowDownRight, ArrowUp, ArrowUpRight, BarChart3, Download, Info, Minus, Table2 } from "lucide-react";
import * as React from "react";
import { Card } from "@/components/ui/card";
import { Sparkline } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { saveBlob } from "@/lib/api";
import { exportName, toCsv, type CsvColumn, type Delta } from "@/lib/reports";
import { cn } from "@/lib/utils";

/**
 * The furniture every report page is built from: a stat tile with its delta, a section card with a
 * chart and its table, a sortable table, and the registry that lets the page's Export menu offer
 * every section's CSV at once.
 */

/**
 * The empty list a report falls back to while it loads. One constant rather than a fresh `[]`:
 * an export built from a fresh array is a new export every render, which registers itself with
 * the page, which renders again.
 */
export const NONE: readonly never[] = [];

// --- exports -----------------------------------------------------------------------------------

export interface ReportExport {
  id: string;
  label: string;
  filename: string;
  csv: () => string;
}

interface Registry {
  register: (entry: ReportExport) => () => void;
  /** Words for a file name: the tab and the window, so two downloads never collide. */
  nameParts: readonly string[];
}

const ExportContext = React.createContext<Registry | null>(null);

/** Holds what the sections below it offer to export, for the page's Export menu. */
export function ExportProvider({
  nameParts, onChange, children,
}: {
  nameParts: readonly string[];
  onChange: (entries: ReportExport[]) => void;
  children: React.ReactNode;
}) {
  const entries = React.useRef(new Map<string, ReportExport>());
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const registry = React.useMemo<Registry>(
    () => ({
      nameParts,
      register: (entry) => {
        entries.current.set(entry.id, entry);
        onChangeRef.current([...entries.current.values()]);
        return () => {
          if (entries.current.get(entry.id) === entry) {
            entries.current.delete(entry.id);
            onChangeRef.current([...entries.current.values()]);
          }
        };
      },
    }),
    // Keyed on the words, not the array: a new array with the same words re-registers nothing.
    [nameParts.join("|")],
  );
  return <ExportContext.Provider value={registry}>{children}</ExportContext.Provider>;
}

export interface SectionExport<T> {
  columns: readonly CsvColumn<T>[];
  rows: readonly T[];
}

function useSectionExport<T>(id: string, label: string, data: SectionExport<T> | undefined): (() => void) | null {
  const registry = React.useContext(ExportContext);
  const entry = React.useMemo<ReportExport | null>(() => {
    if (!data || !registry) return null;
    return {
      id,
      label,
      filename: exportName([...registry.nameParts, id], "csv"),
      csv: () => toCsv(data.columns, data.rows),
    };
  }, [id, label, data, registry]);
  React.useEffect(() => (entry && registry ? registry.register(entry) : undefined), [entry, registry]);
  if (!entry) return null;
  return () => saveBlob(new Blob([entry.csv()], { type: "text/csv;charset=utf-8" }), entry.filename);
}

// --- stat tiles --------------------------------------------------------------------------------

export function DeltaBadge({ delta, context }: { delta: Delta | null; context?: string }) {
  if (!delta) return null;
  // The arrow says which way it moved; the colour says whether that is good.
  const up = delta.label.startsWith("+") || delta.label === "new";
  const down = delta.label.startsWith("−");
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span
      title={context}
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        delta.tone === "good" && "text-success-11",
        delta.tone === "bad" && "text-danger-11",
        (delta.tone === "flat" || delta.tone === "none") && "text-text-muted",
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {delta.label}
      <span className="sr-only">{context}</span>
    </span>
  );
}

/**
 * One headline number: its label, the value, how it moved against the comparison window, a hint,
 * and the trend under it. `info` is the definition, one hover away — a number on a dashboard should
 * be something people can argue with.
 */
export function KpiTile({
  label, value, hint, delta, deltaContext, spark, info, loading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  delta?: Delta | null;
  deltaContext?: string;
  spark?: (number | null)[];
  info?: string;
  loading?: boolean;
}) {
  return (
    <Card className="flex min-w-0 flex-col px-4 pb-2 pt-3">
      <div className="flex items-center gap-1">
        <p className="truncate text-2xs font-medium uppercase tracking-wide text-text-subtle">{label}</p>
        {info && (
          <Tooltip content={info}>
            <button type="button" aria-label={`About ${label}`} className="text-text-subtle hover:text-text">
              <Info className="size-3" />
            </button>
          </Tooltip>
        )}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-20" />
      ) : (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-semibold text-text">{value}</span>
          <DeltaBadge delta={delta ?? null} context={deltaContext} />
        </div>
      )}
      <p className="mt-0.5 min-h-4 truncate text-xs text-text-muted">{loading ? "" : hint}</p>
      {spark && spark.length > 1 && !loading && <Sparkline values={spark} className="mt-1" />}
    </Card>
  );
}

export function KpiRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>;
}

// --- sections ----------------------------------------------------------------------------------

/**
 * A card holding one part of a report: its chart, the same numbers as a table (a click away, never
 * instead of the chart), a CSV of that table, and a note for whatever the numbers cannot say.
 */
export function ReportSection<T>({
  id, title, description, info, actions, table, exportData, note, loading, empty, children, className,
}: {
  id: string;
  title: string;
  description?: React.ReactNode;
  info?: string;
  actions?: React.ReactNode;
  /** The numbers as a table. With a chart as well, a switch flips between them. */
  table?: React.ReactNode;
  exportData?: SectionExport<T>;
  note?: React.ReactNode;
  loading?: boolean;
  empty?: string | false;
  children?: React.ReactNode;
  className?: string;
}) {
  const [view, setView] = React.useState<"chart" | "table">("chart");
  const download = useSectionExport(id, title, exportData);
  const both = Boolean(children) && Boolean(table);
  return (
    <Card id={`report-${id}`} className={cn("report-section min-w-0 overflow-hidden", className)}>
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-0.5">
          <h3 className="flex items-center gap-1.5 text-base font-semibold text-text">
            {title}
            {info && (
              <Tooltip content={info}>
                <button type="button" aria-label={`About ${title}`} className="text-text-subtle hover:text-text">
                  <Info className="size-3.5" />
                </button>
              </Tooltip>
            )}
          </h3>
          {description && <p className="text-xs text-text-muted">{description}</p>}
        </div>
        <div className="report-print-hide flex shrink-0 flex-wrap items-center gap-2">
          {actions}
          {both && (
            <Segmented
              label={`${title}: show as`}
              value={view}
              onChange={(v) => setView(v as "chart" | "table")}
              options={[
                { id: "chart", label: "Chart", icon: BarChart3 },
                { id: "table", label: "Table", icon: Table2 },
              ]}
              iconOnly
            />
          )}
          {download && (
            <Tooltip content="Download as CSV">
              <button
                type="button"
                onClick={download}
                aria-label={`Download ${title} as CSV`}
                className="inline-flex size-7 items-center justify-center rounded-md border border-border bg-surface text-text-muted shadow-xs hover:bg-surface-hover hover:text-text"
              >
                <Download className="size-3.5" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : empty ? (
          <p className="py-10 text-center text-sm text-text-subtle">{empty}</p>
        ) : both ? (
          view === "chart" ? children : table
        ) : (
          (children ?? table)
        )}
        {note && !loading && <p className="mt-3 text-xs text-text-muted">{note}</p>}
      </div>
    </Card>
  );
}

export function Segmented({
  label, value, onChange, options, iconOnly = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { id: string; label: string; icon?: React.ComponentType<{ className?: string }> }[];
  iconOnly?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-border bg-surface-sunken p-0.5">
      {options.map((o) => {
        const Icon = o.icon;
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={iconOnly ? o.label : undefined}
            title={iconOnly ? o.label : undefined}
            onClick={() => onChange(o.id)}
            className={cn(
              "inline-flex h-6 items-center gap-1 rounded px-2 text-xs transition-colors",
              on ? "bg-surface text-text shadow-xs" : "text-text-muted hover:text-text",
            )}
          >
            {Icon && <Icon className="size-3.5" />}
            {!iconOnly && o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A few labelled figures in a row, for the facts beside a chart. */
export function Figures({ items }: { items: readonly { label: string; value: React.ReactNode; hint?: string }[] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((i) => (
        <div key={i.label} className="min-w-0">
          <dt className="truncate text-2xs uppercase tracking-wide text-text-subtle" title={i.label}>
            {i.label}
          </dt>
          <dd className="text-lg font-semibold text-text">{i.value}</dd>
          {i.hint && <dd className="truncate text-xs text-text-muted">{i.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** A share drawn beside its number in a table cell. */
export function ShareBar({ value, tone = "accent" }: { value: number | null; tone?: "accent" | "warning" | "danger" }) {
  const share = value == null ? 0 : Math.max(0, Math.min(1, value));
  return (
    <span className="ml-2 inline-block h-1.5 w-12 overflow-hidden rounded-full bg-gray-4 align-middle" aria-hidden>
      <span
        className={cn(
          "block h-full rounded-full",
          tone === "accent" && "bg-accent-9",
          tone === "warning" && "bg-warning-9",
          tone === "danger" && "bg-danger-9",
        )}
        style={{ width: `${share * 100}%` }}
      />
    </span>
  );
}

// --- the sortable table ------------------------------------------------------------------------

export interface DataColumn<T> {
  key: string;
  header: string;
  /** Drawn before the header's words, never in the CSV: an outcome group's dot (`OutcomeDot`). */
  mark?: React.ReactNode;
  /** What the column sorts on, and what the CSV holds unless `csv` says otherwise. */
  value: (row: T) => string | number | null | undefined;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "right";
  /** A hover definition on the header. */
  title?: string;
  sortable?: boolean;
  className?: string;
}

export function tableExport<T>(columns: readonly DataColumn<T>[], rows: readonly T[]): SectionExport<T> {
  return { columns: columns.map((c) => ({ header: c.header, value: c.value })), rows };
}

/**
 * A table of report rows: click a header to sort (again to flip), numbers right-aligned and in
 * tabular figures, the longest lists cut to their top rows until asked for the rest.
 */
export function DataTable<T>({
  rows, columns, rowKey, initialSort, onRowClick, limit = 25, footer, emptyText = "Nothing in this window",
}: {
  rows: readonly T[];
  columns: readonly DataColumn<T>[];
  rowKey: (row: T) => string;
  initialSort?: { key: string; dir: "asc" | "desc" };
  onRowClick?: (row: T) => void;
  limit?: number;
  footer?: React.ReactNode;
  emptyText?: string;
}) {
  const [sort, setSort] = React.useState(initialSort ?? null);
  const [all, setAll] = React.useState(false);
  const sorted = React.useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = col.value(a);
      const y = col.value(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * factor;
      return String(x).localeCompare(String(y)) * factor;
    });
  }, [rows, columns, sort]);
  const shown = all ? sorted : sorted.slice(0, limit);
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-text-subtle">{emptyText}</p>;
  return (
    <div className="-mx-4 -mb-4">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const Arrow = active ? (sort?.dir === "asc" ? ArrowUp : ArrowDown) : null;
              const sortable = c.sortable ?? true;
              return (
                <TableHead
                  key={c.key}
                  className={cn(c.align === "right" && "text-right", c.className)}
                  aria-sort={active ? (sort?.dir === "asc" ? "ascending" : "descending") : undefined}
                  title={c.title}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={cn("inline-flex items-center gap-0.5 uppercase hover:text-text", active && "text-text")}
                      onClick={() =>
                        setSort((prev) =>
                          prev?.key === c.key
                            ? { key: c.key, dir: prev.dir === "asc" ? "desc" : "asc" }
                            : { key: c.key, dir: c.align === "right" ? "desc" : "asc" },
                        )
                      }
                    >
                      {c.mark}
                      {c.header}
                      {Arrow && <Arrow className="size-3" aria-hidden />}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {c.mark}
                      {c.header}
                    </span>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((row) => (
            <TableRow
              key={rowKey(row)}
              interactive={Boolean(onRowClick)}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  className={cn("whitespace-nowrap", c.align === "right" && "text-right tabular-nums", c.className)}
                >
                  {c.render ? c.render(row) : (c.value(row) ?? "—")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {footer}
      </Table>
      {rows.length > limit && (
        <div className="report-print-hide flex justify-center border-t border-border py-2">
          <button type="button" className="text-xs text-accent-11 hover:underline" onClick={() => setAll((v) => !v)}>
            {all ? `Show the top ${limit}` : `Show all ${rows.length}`}
          </button>
        </div>
      )}
    </div>
  );
}
